/**
 * Count Model Utilities
 * 
 * Pure functions for count aggregation, validation, and formatting.
 * LAT-23: classes never sum (mat count and equipment count never added together)
 * LAT-25: below_zero validation names the current count
 * LAT-27: manual and beacon buckets are separate
 */

/**
 * Aggregate entries into counts grouped by (scope, location).
 * Sums deltas per bucket: manual vs beacon (LAT-27).
 * 
 * @param {Array} entries - Array of entry objects with scopeId, locationId, supplyDelta, demandDelta, channel
 * @returns {Object} Map of `${scopeId}:${locationId}` → { supplyManual, demandManual, supplyBeacon, demandBeacon }
 */
export function aggregateCounts(entries) {
  const result = {};

  for (const entry of entries) {
    const key = `${entry.scopeId}:${entry.locationId}`;
    if (!result[key]) {
      result[key] = {
        scopeId: entry.scopeId,
        locationId: entry.locationId,
        supplyManual: 0,
        demandManual: 0,
        supplyBeacon: 0,
        demandBeacon: 0,
      };
    }

    // LAT-27: Route to correct bucket based on channel
    const isManual = entry.channel === 'trucker-app' || entry.channel === 'desktop';
    if (isManual) {
      result[key].supplyManual += entry.supplyDelta || 0;
      result[key].demandManual += entry.demandDelta || 0;
    } else {
      result[key].supplyBeacon += entry.supplyDelta || 0;
      result[key].demandBeacon += entry.demandDelta || 0;
    }
  }

  return result;
}

/**
 * Validate that a delta would not make a bucket go below zero.
 * LAT-25: Returns the current count in the result so the error can name it.
 * 
 * @param {number} currentBucketCount - Current count in the bucket
 * @param {number} delta - Proposed change (negative for reduction)
 * @returns {{ valid: boolean, currentCount: number, resultingCount: number }}
 */
export function validateBelowZero(currentBucketCount, delta) {
  const current = currentBucketCount || 0;
  const resulting = current + delta;
  return {
    valid: resulting >= 0,
    currentCount: current,
    resultingCount: resulting,
  };
}

/**
 * Format a count for display with locale-aware number formatting.
 * 
 * @param {number} n - The count to format
 * @returns {string} Formatted count string
 */
export function formatCount(n) {
  if (n === null || n === undefined) return '0';
  return new Intl.NumberFormat().format(n);
}

/**
 * Assertion helper: mat count and equipment count never added together.
 * LAT-23: Classes never sum.
 * 
 * @param {Array} counts - Array of count objects with assetClass
 * @returns {{ valid: boolean, classes: string[] }}
 */
export function classesNeverSum(counts) {
  const classes = new Set();
  for (const count of counts) {
    if (count.assetClass) {
      classes.add(count.assetClass);
    }
  }
  const classArray = Array.from(classes);
  return {
    valid: classArray.length <= 1,
    classes: classArray,
  };
}
